import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { FilePlus2, Eye, ChevronLeft, Search } from 'lucide-react';
import { getAssignedCandidates } from '../../../services/admin/api';
import './AssignedCandidates.css';

const AssignedCandidates = () => {
  const { employeeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const employeeName = location.state?.employeeName || 'Unknown';

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortColumn, setSortColumn] = useState('candidateId');
  const [sortOrder, setSortOrder] = useState('desc');
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isAsc, setIsAsc] = useState(false);

  const fetchCandidates = async (
    page = 0,
    size = recordsPerPage,
    sortColumnParam = sortColumn,
    sort = isAsc
  ) => {
    try {
      setLoading(true);
      setError(null);

      page = Math.max(0, page);

      const res = await getAssignedCandidates(
        employeeId,
        page,
        size,
        sortColumnParam,
        sort
      );

      if (res.data?.content) {
        setCandidates(res.data.content);
        setTotalPages(res.data.totalPages);
        setTotalElements(res.data.totalElements);
        setCurrentPage(res.data.number + 1); // backend is 0-based
      }
    } catch (err) {
      setError('Failed to load assigned candidates.');
      setCandidates([]);
      setTotalPages(1);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchCandidates(currentPage - 1, recordsPerPage, sortColumn, isAsc);
    }
  }, [employeeId, currentPage, recordsPerPage]);



  const renderSortArrow = (field) => {
    const isActive = sortColumn === field;

    return (
      <span
        className={`sort-icons ${isActive ? "sort-active" : ""
          }`}
      >
        {/* showing the next action button  */}
        {!isActive
          ? "▲▼"
          : sortOrder === "asc"
            ? "▼" : "▲"
        }
      </span>
    );
  };


  // handle the sort with consistency icons and defualt sort asc . 
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Same column -> toggle
      const newIsAsc = !isAsc;
      setIsAsc(newIsAsc);
      setSortOrder(newIsAsc ? "asc" : "desc");
    } else {
      // Different column -> default to ascending
      setSortColumn(column);
      setIsAsc(true);
      setSortOrder("asc");
    }

    setCurrentPage(0);
  };




  const filteredCandidates = useMemo(() => {
    return [...candidates]
      .filter((c) =>
        c.candidateId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.toString().includes(searchTerm) ||
        c.candidateStatus?.toLowerCase().includes(searchTerm.toLowerCase())
      )

  }, [candidates, searchTerm, sortColumn, sortOrder]);

  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedCandidates = filteredCandidates.slice(
    startIndex,
    startIndex + recordsPerPage
  );


  const formatStatusClass = (status) =>
    status ? status.toLowerCase().replace(/_/g, '-') : '';

  return (
    <div className="assigned-candidates-container">

      <div className="assigned-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ChevronLeft size={18} /> Back
        </button>
      </div>

      <div className="assigned-candidate-table-controls">
        <div className="assigned-candidate-entries-select">
          Show
          <select
            value={recordsPerPage}
            onChange={(e) => {
              setRecordsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          entries
        </div>

        <div className="assigned-candidate-search-wrapper">
          <input
            type="text"
            placeholder="Search by Candidate Name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <Search className="assigned-candidate-search-icon" size={18} />
        </div>
      </div>

      <div className="table-container">
        <table className="assigned-candidates-table">
          <thead>
            <tr>
              {[
                ['candidateId', 'Candidate ID'],
                ['fullName', 'Candidate Name'],
                ['clientName', 'Client Name'],
                ['email', 'Email'],
                ['phone', 'Phone'],
                ['assignedAt', 'Assigned At'],
                ['currentBgcStatus', 'Status'],
              ].map(([key, label]) => (
                <th key={key} onClick={() => handleSort(key)}>
                  {label} {renderSortArrow(key)}
                  {/* {sortColumn === key ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'} */}
                </th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="no-data loading-text">
                  Loading assigned candidates...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="8" className="no-data">{error}</td>
              </tr>
            ) : filteredCandidates.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  No candidates assigned to this employee.
                </td>
              </tr>
            ) : (
              filteredCandidates.map((cand) => (
                <tr key={cand.id}>
                  <td>{cand.candidateId}</td>
                  <td>{cand.fullName}</td>
                  <td>{cand.clientName}</td>
                  <td>{cand.email}</td>
                  <td>{cand.phone}</td>
                  <td>{new Date(cand.assignedAt).toLocaleString()}</td>
                  <td className={`status-cell ${formatStatusClass(cand.candidateStatus)}`}>
                    {cand.candidateStatus}
                  </td>
                  <td className="action-icons">
                    <button
                      className={`icon-btn ${cand.candidateStatus !== 'VERIFIED' ? 'disabled' : ''}`}
                      disabled={cand.candidateStatus !== 'VERIFIED'}
                      title="View Candidate Report"
                      onClick={() =>
                        navigate(`/admin/reports/${cand.candidateId}`, {
                          state: { heading: 'Candidate Report', candidate: cand },
                        })
                      }
                    >
                      <FilePlus2 size={18} />
                    </button>

                    <button
                      className="icon-btn"
                      title="View Candidate Details"
                      onClick={() =>
                        navigate(`/admin/candidate-details/${cand.candidateId}`, {
                          state: { candidate: cand },
                        })
                      }
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="assigned-candidate-pagination">
        <div className="assigned-candidate-pagination-summary">
          Showing {(currentPage - 1) * recordsPerPage + 1}
          to {Math.min(currentPage * recordsPerPage, totalElements)}
          of {totalElements} entries
        </div>

        <div className="assigned-candidate-pagination-buttons">
          {/* Previous */}
          <button
            className="assigned-candidate-page-btn"
            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          {/* Page Numbers */}
          {Array.from({ length: totalPages }, (_, index) => {
            const pageNumber = index + 1;
            return (
              <button
                key={pageNumber}
                className={`assigned-candidate-page-btn ${currentPage === pageNumber ? "active" : ""
                  }`}
                onClick={() => setCurrentPage(pageNumber)}
              >
                {pageNumber}
              </button>
            );
          })}

          {/* Next */}
          <button
            className="assigned-candidate-page-btn"
            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignedCandidates;
